/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — / (public landing), /news and
 * /news/:slug (public dispatches), /legal/:slug (the landing page with a
 * policy open over it), /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated),
 * and /admin-panel (authenticated, ADMIN only — the built-in admin panel;
 * `/admin` is NOT ours, Caddy proxies it to the Kottster container).
 *
 * Game developers wire their rendering engine inside mountGame() — replace
 * the placeholder with PixiJS, Babylon.js, or DOM rendering.
 *
 * ─── `/` is the landing page, for everyone ──────────────────────────────────
 * It used to be an alias for /play that bounced signed-out visitors to /login,
 * which meant the site root was a login form. It is now the public landing
 * page, and it is NOT signed-out-only: a signed-in player following a link to
 * the root should see the page, with its call to action reading "Continue"
 * instead of "Play free", rather than being redirected away from it. /play
 * remains the game and remains guarded.
 *
 * ─── /legal/:slug is not a page ─────────────────────────────────────────────
 * The policies open as a sheet OVER the landing page, not as a screen of their
 * own. The path still exists so a link to one is shareable and the back button
 * works: it renders the landing page and tells it which document to open. See
 * `renderLanding`'s `openLegal` option.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { initAnalytics, trackPageView } from './core/analytics/gtag';
import { mountGame, unmountGame } from './engine/bootstrap';
import { adminRouteAllowed, renderAdmin } from './engine/admin/admin-ui';
import { renderLanding } from './engine/landing';
import { renderNewsIndex, renderNewsArticle } from './engine/landing/news-view';
import { renderLegalPage } from './engine/landing/legal-view';
import { legalPath, legalSlugForPath } from './engine/landing/legal-paths';
import { mountAuthChrome } from './engine/landing/auth-chrome';
import { syncBackdrop } from './engine/landing/landing-backdrop';

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

function render() {
  const path = location.pathname;
  // BEFORE the teardown below, deliberately. The settlement behind the public
  // pages is a layer this router owns rather than any page, and it has to
  // survive the swap: the camera pans from the landing page into the login
  // form, and a scene torn down between the two would be rebuilt on the far
  // side instead of travelling. See `landing-backdrop.ts`.
  syncBackdrop(path);

  teardown?.();
  teardown = null;

  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    navigate('/play');
  };

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = withAuthChrome(renderLogin(app, afterAuth));
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = withAuthChrome(renderRegister(app, afterAuth));
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = withAuthChrome(renderForgotPassword(app));
      break;
    case '/reset-password':
      teardown = withAuthChrome(renderResetPassword(app, params.get('token') ?? '', () => navigate('/login')));
      break;
    case '/verify-email':
      teardown = withAuthChrome(renderVerifyEmail(app, params.get('token') ?? ''));
      break;
    case '/check-email':
      if (!user) { navigate('/login'); return; }
      teardown = withAuthChrome(
        renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); }),
      );
      break;
    case '/':
      teardown = renderLanding(app, { navigate });
      break;
    case '/news':
      teardown = renderNewsIndex(app, { navigate });
      break;
    case '/play':
      if (!user) { navigate('/login'); return; }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      connectSocket();
      teardown = mountGame(app, user, () => { logout(); disconnectSocket(); navigate('/login'); });
      break;
    case '/admin-panel':
      // The built-in admin panel.
      //
      // `/admin-panel`, NOT `/admin`, and the distinction is load-bearing:
      // Caddy still proxies `/admin/*` to the Kottster container, and the house
      // deploy pipeline (`devops/after-humanity/docker`) still builds, pulls and
      // routes that image. Taking the path would have made this route
      // unreachable behind the proxy in every deployed environment while
      // looking perfectly fine in `vite dev`, which has no Caddy in front of it.
      // Caddy's `/admin/*` matcher needs the trailing slash, so `/admin-panel`
      // falls through to the SPA catch-all untouched.
      //
      // Bouncing a non-admin to /play rather than /login is deliberate: a
      // signed-in player who follows a stale link has not failed to
      // authenticate, and sending them to a login form they are already past
      // would be nonsense. The panel's endpoints are guarded again server-side
      // by RolesGuard — this check only decides what to draw.
      if (!user) { navigate('/login'); return; }
      if (!adminRouteAllowed()) { navigate('/play'); return; }
      teardown = renderAdmin(app, { navigate });
      break;
    default:
      // Policies are pages of their own. The three the seed writes live at
      // `/terms-and-conditions`, `/privacy-policy` and `/cookie-policy`;
      // anything else an operator publishes is `/legal/<slug>`. The old
      // `/legal/<known-slug>` URL is rewritten to the canonical one in place.
      // A slug whose row is missing or deactivated still renders the page,
      // which then says so — the endpoint fails closed and silently.
      {
        const legalSlug = legalSlugForPath(path);
        if (legalSlug) {
          const canonical = legalPath(legalSlug);
          if (canonical !== path) history.replaceState(null, '', canonical);
          teardown = renderLegalPage(app, legalSlug, { navigate });
          break;
        }
      }
      if (path.startsWith('/news/')) {
        teardown = renderNewsArticle(app, decodeURIComponent(path.slice('/news/'.length)), { navigate });
        break;
      }
      navigate('/login');
      return;
  }

  // Redirect branches returned above, so only the screen actually shown counts.
  trackPageView(path + location.search);
}

/**
 * Adds the brand and the back-to-landing arrow to a core auth screen.
 *
 * `core/**` is template-owned and cannot be edited in a game repo, so the six
 * screens are decorated from out here instead: core renders its card, and this
 * mounts the chrome into the same wrapper afterwards. Takes and returns the
 * screen's own teardown so the two come down together.
 */
function withAuthChrome(screenTeardown: () => void): () => void {
  const chromeTeardown = mountAuthChrome(app, { navigate });
  return () => {
    chromeTeardown();
    screenTeardown();
  };
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.origin !== location.origin) return;
  // An in-page anchor is a SCROLL, not a route. This handler used to swallow
  // every same-origin link, so the landing page's own menu — `href="#gear"` and
  // friends, whose `pathname` is just `/` — re-rendered the whole page and
  // scrolled nowhere. Left to the browser, it scrolls and updates the hash.
  //
  // The pathname comparison is the load-bearing half: a link to `/news#latest`
  // from `/` is a real navigation and must still route.
  if (a.hash && a.pathname === location.pathname) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();
  // Preview/sandbox builds never report into the game's analytics property.
  else initAnalytics();

  await bootstrapSession();
  render();

  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') navigate('/login');
  });
})();

window.addEventListener('beforeunload', () => {
  teardown?.();
  unmountGame();
});
